import { NavLink } from 'react-router-dom'
import { ChevronRight, Book, FileText, Library } from 'lucide-react'
import { useState } from 'react'
import type { WikiTree as WikiTreeData } from '../types/wiki'

function WikiTreeShelf({
  shelf,
  depth,
}: {
  shelf: WikiTreeData['shelves'][number]
  depth: number
}) {
  const [open, setOpen] = useState(depth < 1)

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 w-full text-left py-2 px-2 rounded-md text-sm text-gray-400 hover:bg-gray-800/60 min-h-[44px]"
        style={{ paddingLeft: 8 + depth * 10 }}
        aria-expanded={open}
      >
        <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        <Library className="w-4 h-4 shrink-0 text-primary-500/80" />
        <NavLink
          to={`/s/${shelf.slug}`}
          onClick={(e) => e.stopPropagation()}
          className="truncate font-medium text-gray-300 hover:text-primary-300"
        >
          {shelf.name}
        </NavLink>
      </button>
      {open && (
        <div className="border-l border-gray-800/80 ml-3">
          {shelf.books.map((book) => (
            <WikiTreeBook key={book.id} book={book} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

function WikiTreeBook({
  book,
  depth,
}: {
  book: WikiTreeData['shelves'][number]['books'][number]
  depth: number
}) {
  const [open, setOpen] = useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 w-full text-left py-2 px-2 rounded-md text-sm text-gray-400 hover:bg-gray-800/60 min-h-[44px]"
        style={{ paddingLeft: 8 + depth * 10 }}
        aria-expanded={open}
      >
        <ChevronRight className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
        <Book className="w-4 h-4 shrink-0 text-amber-600/90" />
        <NavLink
          to={`/b/${book.slug}`}
          onClick={(e) => e.stopPropagation()}
          className="truncate text-gray-300 hover:text-primary-300"
        >
          {book.name}
        </NavLink>
      </button>
      {open && (
        <div className="border-l border-gray-800/80 ml-3">
          {book.chapters.map((chapter) =>
            chapter.pages.map((page) => (
              <NavLink
                key={page.id}
                to={`/p/${page.slug}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 py-2 px-2 rounded-md text-sm min-h-[44px] touch-manipulation ${
                    isActive
                      ? 'bg-primary-900/40 text-primary-200 border border-primary-700/40'
                      : 'text-gray-300 hover:bg-gray-800/80 border border-transparent'
                  } ${!page.published ? 'opacity-60 italic' : ''}`
                }
                style={{ paddingLeft: 8 + (depth + 2) * 10 }}
              >
                <FileText className="w-4 h-4 shrink-0 text-gray-500" />
                <span className="truncate">{page.title}</span>
              </NavLink>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default function WikiTree({ tree }: { tree: WikiTreeData }) {
  if (!tree.shelves.length) {
    return <p className="text-gray-500 text-sm px-2">No wiki shelves. Run bootstrap when DB is configured.</p>
  }

  return (
    <nav aria-label="Wiki tree" className="space-y-1 pb-8">
      {tree.shelves.map((shelf) => (
        <WikiTreeShelf key={shelf.id} shelf={shelf} depth={0} />
      ))}
    </nav>
  )
}
